/**
 * Проверка самостоятельных заданий (E4.6c): цель засчитывается по порядку, только по
 * действию пользователя в своём учебном экземпляре и только когда ответ сервера
 * подтверждает результат (view -- сохранённое состояние, а не черновик формы). Условия
 * перенесены из замороженного макета (training-exercises.js) на данные сценария
 * platform-v1.
 */

import type { TrainingManifest, TrainingView } from '@/api/training';
import i18n from '@/utils/i18n';
import ruLearning from '@/locales/ru/learning.json';
import enLearning from '@/locales/en/learning.json';
import { ACCESS_RESOURCE_KINDS } from '@/api/accessPolicyTypes';
import { EXERCISES, type ExerciseId } from './catalog';
import type { TrainingObservation } from './observations';
import { recordExerciseGoal, type TrainingProgress } from './progress';

export type { TrainingObservation } from './observations';

/**
 * Названия учебной политики из словарей: пользователь вводит имя на своём языке;
 * проверка принимает оба, чтобы смена языка не делала сохранённую политику неверной
 */
export const TRAINING_POLICY_NAME = ruLearning.exercises.policy.name;
const POLICY_NAMES: readonly string[] = [TRAINING_POLICY_NAME, enLearning.exercises.policy.name];

/** Параметры текстов заданий: имя, которое проверяет задание «Учебная политика» */
export function exerciseTextParams() {
	return { policyName: i18n.t('learning:exercises.policy.name') };
}

/** Типы данных категории PII сценария (scenario-v1.json, category: pii) */
export const PII_DATA_TYPES = [
	'email',
	'phone',
	'passport',
	'ip_address',
	'person',
	'address',
	'birth_date',
] as const;

const EXPORTS = 'exports';
const PRIMARY_EVENT = 'EV-1042';
const ASSIGNEE = 'ak';
const PROD_CLUSTER = 'prod-cluster';

type Json = Record<string, unknown>;

function data(view: TrainingView): Json {
	return (view.data ?? {}) as Json;
}

function sameSet(actual: unknown, expected: readonly string[]): boolean {
	if (!Array.isArray(actual) || actual.length !== expected.length) return false;
	const values = new Set(actual);
	return values.size === expected.length && expected.every((value) => values.has(value));
}

type Check = (
	observation: TrainingObservation,
	manifest: TrainingManifest,
	view: TrainingView,
) => boolean;

/** Учебная политика с точными параметрами условия задания */
function exactTrainingPolicy(manifest: TrainingManifest, view: TrainingView): boolean {
	const policy = data(view);
	const access = (policy.access ?? {}) as Json;
	const scope = (access.scope ?? {}) as Json;
	const pii = PII_DATA_TYPES.map((key) => manifest.data_types[key]);
	return (
		view.subject === 'policy' &&
		policy.type === 'access' &&
		typeof policy.name === 'string' &&
		POLICY_NAMES.includes(policy.name) &&
		policy.severity === 'high' &&
		policy.is_active === false &&
		Array.isArray(scope.resource_kinds) &&
		scope.resource_kinds.length > 0 &&
		scope.resource_kinds.every((kind) => ACCESS_RESOURCE_KINDS.includes(kind)) &&
		pii.every((value) => value !== undefined) &&
		sameSet(scope.data_type_ids, pii)
	);
}

const GOALS: Readonly<Record<ExerciseId, Readonly<Record<string, Check>>>> = {
	daily: {
		/* customer-exports открыт пользователем на карте, сервер подтверждает его кластер */
		resource: (o, m, v) =>
			o.kind === 'resource-visible' &&
			o.entity_id === m.resources[EXPORTS]?.id &&
			view(v, 'resource') &&
			((data(v).placement as Json | undefined)?.cluster_id ?? null) === m.clusters[PROD_CLUSTER],
		event: (o, m, v) =>
			o.kind === 'event-opened' &&
			o.entity_id === m.events[PRIMARY_EVENT] &&
			view(v, 'event') &&
			data(v).id === o.entity_id,
		/* Разбор сохранён на сервере: статус «В работе» и учебный Алексей Козлов */
		assignment: (o, m, v) =>
			o.kind === 'event-saved' &&
			o.entity_id === m.events[PRIMARY_EVENT] &&
			view(v, 'event_workflow') &&
			data(v).status === 'in_progress' &&
			data(v).assignee_id === m.actors[ASSIGNEE],
	},
	policy: {
		saved: (o, m, v) =>
			o.kind === 'policy-saved' && data(v).id === o.entity_id && exactTrainingPolicy(m, v),
	},
	ai: {
		masked: (o, m, v) =>
			o.kind === 'llm-event-opened' &&
			o.entity_id === m.llm_events.masked &&
			view(v, 'llm_event') &&
			data(v).outcome === 'masked',
		blocked: (o, m, v) =>
			o.kind === 'llm-event-opened' &&
			o.entity_id === m.llm_events.blocked &&
			view(v, 'llm_event') &&
			data(v).outcome === 'blocked',
	},
};

function view(v: TrainingView, subject: string): boolean {
	return v.subject === subject;
}

/**
 * Следующая цель засчитывается, если действие пользователя и ответ сервера ей соответствуют.
 * Действие учитывается только в активном экземпляре прогресса: цель записывается под ним, и
 * действие в прежнем примере (другая вкладка уже создала новый) новому не засчитывается.
 */
export function advanceExercise(
	progress: TrainingProgress,
	id: ExerciseId,
	observation: TrainingObservation,
	manifest: TrainingManifest,
	trainingView: TrainingView,
): TrainingProgress {
	if (
		observation.source !== 'user' ||
		observation.instance_id !== manifest.instance_id ||
		manifest.instance_id !== progress.active_instance_id
	)
		return progress;
	const done = progress.exercises[id]?.done ?? [];
	const goal = EXERCISES[id].goals[done.length];
	if (goal === undefined || !GOALS[id][goal](observation, manifest, trainingView)) return progress;
	return recordExerciseGoal(progress, id, goal);
}
